// UnifiedNavigationButtons.tsx
import { Button } from "@shared/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { ReactNode } from "react";

interface NavigationButtonsProps {
  // Back button
  onBack: () => void;
  backText?: string;
  showBackIcon?: boolean;

  // Continue button
  onContinue: () => void;
  continueText?: string | ReactNode;
  canContinue?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  showContinueIcon?: boolean;

  // Additional buttons (for ActionButtons case)
  additionalButtons?: ReactNode;

  // Styling variants
  variant?: "default" | "compact";
}

export const NavigationButtons = ({
  onBack,
  backText = "Back",
  showBackIcon = true,

  onContinue,
  continueText = "Continue",
  canContinue = true,
  isLoading = false,
  loadingText = "Processing...",
  showContinueIcon = true,

  additionalButtons,

  variant = "default",
}: NavigationButtonsProps) => {
  const paddingClass = variant === "compact" ? "px-4 py-2" : "px-6 py-2";

  return (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      {/* Back Button */}
      <Button onClick={onBack} variant="outline">
        {showBackIcon && <ArrowLeft size={16} />}
        {backText}
      </Button>

      {/* Right Side: Additional Buttons + Continue */}
      <div className="flex gap-3 items-center">
        {additionalButtons}

        <Button
          onClick={onContinue}
          disabled={!canContinue || isLoading}
          className={`${paddingClass} rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
            canContinue && !isLoading
              ? "bg-blue-600 hover:bg-blue-700 text-white hover:shadow-lg hover:-translate-y-0.5"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isLoading ? (
            <>
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              {loadingText}
            </>
          ) : (
            <>
              {continueText}
              {showContinueIcon && <ArrowRight size={16} />}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};
